import React, { useEffect, useState } from 'react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';

// Imperative confirm() helper backed by shadcn's AlertDialog.
// Usage:
//   const ok = await confirmDialog({ title, description, actionLabel, cancelLabel, tone });
//   if (!ok) return;

let listeners = [];
let currentResolve = null;

const emit = (state) => {
  listeners.forEach((cb) => cb(state));
};

export const confirmDialog = ({
  title = 'Are you sure?',
  description = '',
  actionLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'default', // 'destructive' shows a red action button
  testId = 'confirm-dialog',
} = {}) =>
  new Promise((resolve) => {
    if (currentResolve) currentResolve(false);
    currentResolve = resolve;
    emit({ open: true, title, description, actionLabel, cancelLabel, tone, testId });
  });

export const ConfirmDialogHost = () => {
  const [state, setState] = useState({ open: false });

  useEffect(() => {
    const cb = (s) => setState(s);
    listeners.push(cb);
    return () => {
      listeners = listeners.filter((l) => l !== cb);
    };
  }, []);

  const close = (value) => {
    const resolver = currentResolve;
    currentResolve = null;
    setState((s) => ({ ...s, open: false }));
    if (resolver) resolver(value);
  };

  const {
    open, title, description, actionLabel, cancelLabel, tone, testId,
  } = state;

  return (
    <AlertDialog open={!!open} onOpenChange={(o) => { if (!o) close(false); }}>
      <AlertDialogContent data-testid={testId}>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {description ? <AlertDialogDescription>{description}</AlertDialogDescription> : null}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`${testId}-cancel`} onClick={() => close(false)}>
            {cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid={`${testId}-action`}
            onClick={() => close(true)}
            className={tone === 'destructive' ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500' : ''}
          >
            {actionLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ConfirmDialogHost;
